import {Button} from '@dropbox/dig-components/buttons';
import {Checkbox} from '@dropbox/dig-components/controls';
import {Menu} from '@dropbox/dig-components/menu';
import {Text} from '@dropbox/dig-components/typography';
import {Box} from '@dropbox/dig-foundations';
import {ApiError} from 'client';
import {t} from 'i18next';
import {NotFound} from 'pages/NotFound';
import {useTimeframesQuery} from 'views/workstreams/detail/hooks';
import type {StatusValue} from 'views/workstreams/detail/types';
import {
  formatWorkstreamTimeframeLabel,
  getNavigationTimeframes,
  WorkstreamTimeframe,
} from 'views/workstreams/detail/workstreamTimeframe';

export const TimeframeDropdown = ({
  value,
  onChange,
}: {
  value: WorkstreamTimeframe;
  onChange: (next: WorkstreamTimeframe) => void;
}) => {
  const {timeframes: allTimeframes, isLoading, isError} = useTimeframesQuery();
  const timeframes = getNavigationTimeframes(allTimeframes);
  // A first visit has no cached timeframes, so wait for the API rather than guess.
  if (timeframes.length === 0) {
    return (
      <Button variant="opacity" disabled>
        {isError && !isLoading ? t('timeframes_unavailable') : t('timeframes_loading')}
      </Button>
    );
  }
  return (
    <Menu.Wrapper onSelection={(next: string) => onChange(next as WorkstreamTimeframe)}>
      {({getContentProps, getTriggerProps}) => (
        <>
          <Button {...getTriggerProps()} variant="opacity" withDropdownIcon>
            {formatWorkstreamTimeframeLabel(value)}
          </Button>
          <Menu.Content {...getContentProps()} placement="bottom-end">
            <Menu.Segment>
              {timeframes.map(({name}) => (
                <Menu.ActionItem key={name} value={name}>
                  {formatWorkstreamTimeframeLabel(name)}
                </Menu.ActionItem>
              ))}
            </Menu.Segment>
          </Menu.Content>
        </>
      )}
    </Menu.Wrapper>
  );
};

export const EmptyState = ({title, detail}: {title: string; detail?: string}) => (
  <Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    paddingX="16"
    paddingY="32"
    style={{gap: 4, textAlign: 'center'}}
  >
    <Text size="small" isBold color="standard">
      {title}
    </Text>
    {detail && (
      <Text size="small" color="subtle">
        {detail}
      </Text>
    )}
  </Box>
);

// A pane whose data failed to load, with a retry (like Audit's and Inbox's).
export const LoadError = ({message, onRetry}: {message: string; onRetry: () => void}) => (
  <Box display="flex" flexDirection="column" alignItems="center" paddingY="44" style={{gap: 16}}>
    <Text color="subtle">{message}</Text>
    <Button variant="outline" size="small" onClick={onRetry}>
      {t('retry')}
    </Button>
  </Box>
);

// A workstream that didn't load: Not Found only when it isn't there (or the id
// is bad), so a server error or a dropped connection offers a retry instead.
export const WorkstreamLoadFailure = ({error, onRetry}: {error: unknown; onRetry: () => void}) =>
  !error || (error instanceof ApiError && (error.status === 404 || error.status === 422)) ? (
    <NotFound />
  ) : (
    <LoadError message="Couldn't load this workstream." onRetry={onRetry} />
  );

const STATUS_OPTIONS: {value: StatusValue; label: string}[] = [
  {value: 'off_track', label: 'Off track'},
  {value: 'at_risk', label: 'At risk'},
  {value: 'no_status', label: 'Not started'},
  {value: 'on_track', label: 'On track'},
  {value: 'missed', label: 'Missed'},
  {value: 'cancelled', label: 'Cancelled'},
  {value: 'complete', label: 'Complete'},
];

export const StatusFilterDropdown = ({
  selected,
  includeArchived,
  onChange,
  onArchivedChange,
}: {
  selected: StatusValue[];
  includeArchived: boolean;
  onChange: (next: StatusValue[]) => void;
  onArchivedChange: (next: boolean) => void;
}) => {
  const count = selected.length + (includeArchived ? 1 : 0);
  return (
    <Menu.Wrapper>
      {({getContentProps, getTriggerProps}) => (
        <>
          <Button {...getTriggerProps()} variant="borderless" size="small" withDropdownIcon>
            Status{count > 0 ? ` (${count})` : ''}
          </Button>
          <Menu.Content {...getContentProps()} placement="bottom-end" style={{minWidth: 200}}>
            <Menu.Segment>
              {STATUS_OPTIONS.map(({value, label}) => (
                <Menu.ActionItem
                  key={value}
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onChange(
                      selected.includes(value)
                        ? selected.filter((s) => s !== value)
                        : [...selected, value]
                    );
                  }}
                  withLeftAccessory={
                    <Checkbox checked={selected.includes(value)} readOnly tabIndex={-1} />
                  }
                >
                  {label}
                </Menu.ActionItem>
              ))}
            </Menu.Segment>
            <Menu.Segment>
              <Menu.ActionItem
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onArchivedChange(!includeArchived);
                }}
                withLeftAccessory={<Checkbox checked={includeArchived} readOnly tabIndex={-1} />}
              >
                Include archived
              </Menu.ActionItem>
            </Menu.Segment>
          </Menu.Content>
        </>
      )}
    </Menu.Wrapper>
  );
};
